import { useMemo } from 'react'

interface DataPoint {
  label: string
  value: number
}

interface SimpleBarChartProps {
  data: DataPoint[]
  height?: number
  barColor?: string
  showLabels?: boolean
  formatValue?: (value: number) => string
}

export function SimpleBarChart({
  data,
  height = 200,
  barColor = 'bg-blue-500',
  showLabels = true,
  formatValue = (v) => v.toString(),
}: SimpleBarChartProps) {
  const maxValue = useMemo(() => Math.max(...data.map(d => d.value), 1), [data])

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400"
        style={{ height }}
      >
        No data available
      </div>
    )
  }

  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-1">
        {data.map((point, index) => {
          const barHeight = (point.value / maxValue) * 100
          return (
            <div
              key={index}
              className="flex-1 flex flex-col items-center justify-end h-full group"
            >
              {/* Tooltip */}
              <div className="relative mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  {formatValue(point.value)}
                </div>
              </div>
              {/* Bar */}
              <div
                className={`w-full ${barColor} rounded-t transition-all duration-300 min-h-[2px]`}
                style={{ height: `${Math.max(barHeight, 1)}%` }}
              />
              {/* Label */}
              {showLabels && (
                <span className="text-xs text-gray-500 mt-1 truncate w-full text-center">
                  {point.label}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface SimpleLineChartProps {
  data: DataPoint[]
  height?: number
  lineColor?: string
  fillColor?: string
  showLabels?: boolean
  formatValue?: (value: number) => string
}

export function SimpleLineChart({
  data,
  height = 200,
  lineColor = 'stroke-blue-500',
  fillColor = 'fill-blue-100',
  showLabels = true,
  formatValue = (v) => v.toString(),
}: SimpleLineChartProps) {
  const maxValue = useMemo(() => Math.max(...data.map(d => d.value), 1), [data])

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400"
        style={{ height }}
      >
        No data available
      </div>
    )
  }

  const chartHeight = showLabels ? height - 24 : height
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1 || 1)) * 100,
    y: 100 - (d.value / maxValue) * 100,
    value: d.value,
    label: d.label,
  }))

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')

  const areaD = `${pathD} L 100 100 L 0 100 Z`

  return (
    <div className="w-full" style={{ height }}>
      <div className="relative" style={{ height: chartHeight }}>
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          {/* Grid lines */}
          <line x1="0" y1="25" x2="100" y2="25" className="stroke-gray-200" strokeWidth="0.5" />
          <line x1="0" y1="50" x2="100" y2="50" className="stroke-gray-200" strokeWidth="0.5" />
          <line x1="0" y1="75" x2="100" y2="75" className="stroke-gray-200" strokeWidth="0.5" />

          {/* Area fill */}
          <path d={areaD} className={fillColor} opacity="0.3" />

          {/* Line */}
          <path
            d={pathD}
            fill="none"
            className={lineColor}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />

          {/* Points */}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="1.5"
              className={`${lineColor.replace('stroke-', 'fill-')} hover:r-3 cursor-pointer`}
            />
          ))}
        </svg>

        {/* Hover tooltips */}
        <div className="absolute inset-0 flex">
          {points.map((p, i) => (
            <div
              key={i}
              className="flex-1 group relative"
            >
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  {formatValue(p.value)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Labels */}
      {showLabels && (
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-500">{data[0]?.label}</span>
          <span className="text-xs text-gray-500">{data[data.length - 1]?.label}</span>
        </div>
      )}
    </div>
  )
}

interface StatComparisonProps {
  current: number
  previous: number
  label: string
  formatValue?: (value: number) => string
}

export function StatComparison({
  current,
  previous,
  label,
  formatValue = (v) => v.toString(),
}: StatComparisonProps) {
  const change = previous > 0 ? ((current - previous) / previous) * 100 : 0
  const isPositive = change >= 0

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">{label}:</span>
      <span className="font-semibold">{formatValue(current)}</span>
      {previous > 0 && (
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            isPositive
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {isPositive ? '+' : ''}{change.toFixed(0)}%
        </span>
      )}
    </div>
  )
}
